<script setup>
import IconEye from "@/components/Icons/IconEye.vue";
import {computed, defineEmits, defineProps, onMounted, ref} from "vue";
import {t} from "@/controllerLK/AvalLocalization";
import {
  validateAllowedChars,
  validateNotOnlySpecials,
  validateSpecialPosition,
  validatorEmoji
} from "@/functions/validators";
import RubricQueryParameters
  from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup/RubricQueryParameters.vue";
// import RubricSearchType from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup/RubricSearchType.vue";
import NotifyAlert from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup/NotifyAlert.vue";
import TrashIcon from "@/components/Icons/TrashIcon.vue";
import {useNewsFeedStore} from "@/store/NewsFeed/newsFeed";
import {useRubricsStore} from "@/store/NewsFeed/rubrics";

const props = defineProps({
  rubric: {
    type: [Object, null],
    required: true
  },
  isNewRubric: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["close-edit-popup", "open-delete-popup"]);

onMounted(() => {
  currentEditRubric.value = JSON.parse(JSON.stringify(props.rubric));

  const userShowStartMonitoringNotify = localStorage.getItem("notify_rubrics_start_monitoring");

  if (userShowStartMonitoringNotify) {
    isShowStartMonitoringNotify.value = JSON.parse(userShowStartMonitoringNotify);
  }
});

const newsFeedStore = useNewsFeedStore();
const rubricsStore = useRubricsStore();

// Максимальная длина запроса.
const QUERY_MAX_LENGTH = 1100;

// Флаг отправки формы
const isFormSubmitted = ref(false);

// Начальное состояние рубрики
const currentEditRubric = ref(null);

// Режима поиска рубрики. 0: классический. 1: семантический.
// const currentRubricSearchType = ref(0);

// Наличие изменений в рубрики
const isShowConfirmModal = ref(false);
const hasNewChanges = computed(() =>
    !(JSON.stringify(props.rubric) === JSON.stringify(currentEditRubric.value))
);

// Состояние кнопки сохранения рубрики
const isSaveButtonDisabled = computed(() =>
    !currentEditRubric.value.name.length ||
    !currentEditRubric.value.query.length ||
    JSON.stringify(props.rubric) === JSON.stringify(currentEditRubric.value)
);

// Постановка рубрики на мониторинг
const setRubricFixed = () => {
  if (!currentEditRubric.value.is_fixed) {
    currentEditRubric.value.is_fixed = true;
  }
};

// Закрытие модального окна изменения рубрики
const closeAside = () => {
  if (hasNewChanges.value) {
    isShowConfirmModal.value = true;
  } else {
    emit("close-edit-popup");
  }
};

// Проверки на наличие ошибок, сброс ошибок.
const checkErrorList = () => { return Object.values(errorList.value).some(Boolean); };
const resetErrorList = () => { for (const k in errorList.value) errorList.value[k] = false; };
const errorList = ref({
  nameExist: false,
  nameRequired: false,
  queryExist: false,
  queryRequired: false,
  queryInvalid: false,
  queryMaxLength: false,
  nameEmoji: false,
  nameSymbol: false,
  nameMaxLength: false
});

// Получение типа ошибки на запрос.
const getRubricQueryErrorType = computed(() => {
  if (errorList.value.queryExist) {
    return "query_exist";
  } else if (errorList.value.queryRequired) {
    return "query_required";
  } else if (errorList.value.queryInvalid) {
    return "query_invalid";
  } else if(errorList.value.queryMaxLength) {
    return "query_max_length";
  } else {
    return null;
  }
});

const nameErrorMessage = computed(() => {

  // Проверяем ошибки в порядке приоритета
  if (errorList.value.nameExist) {
    return t("news_page_rubrics_edit_popup.error.unique", currentEditRubric.value.name);
  }

  if (errorList.value.nameEmoji) {
    return t("news_page_rubrics_edit_popup.error.emoji");
  }

  if (errorList.value.nameSymbol && !errorList.value.nameEmoji) {
    return t("news_page_rubrics_edit_popup.error.symbol");
  }

  if (errorList.value.nameRequired) {
    return t("news_page_rubrics_edit_popup.error.required");
  }

  if (errorList.value.nameMaxLength) {
    return t("news_page_rubrics_edit_popup.error.max_length");
  }

  return null;
});

// Computed get/set наименование рубрики с валидацией
const rubricName = computed({
  get() { return currentEditRubric.value.name; },
  set(val) {
    currentEditRubric.value.name = val;
    if (val.length >= 1) validateRubricName(val); // валидация "на лету" при вводе от 1 символа
  }
});

// Computed get/set запрос рубрики с валидацией
const rubricQuery = computed({
  get() { return currentEditRubric.value.query; },
  set(val) {
    currentEditRubric.value.query = val.slice(0, 1200);
    if (val.length >= 1) validateRubricQuery(val);
  }
});

// Валидация запроса рубрики. (Существование и корректность запроса проверяет БЕК)
const validateRubricQuery = (value) => {
  errorList.value.queryRequired = !value.length;
  errorList.value.queryMaxLength = value.length >= 1100;
  errorList.value.queryInvalid = false;
  errorList.value.queryExist = false;
};

// Валидация наименования рубрики.
const validateRubricName = (value) => {
  errorList.value.nameExist = false;
  errorList.value.nameRequired = !value.length;
  errorList.value.nameEmoji = validatorEmoji(value);
  errorList.value.nameSymbol = !(
      validateAllowedChars(value) &&
      validateSpecialPosition(value) &&
      validateNotOnlySpecials(value)
  );
  errorList.value.nameMaxLength = value.length >= 250;
};


// Общий запуск валидации рубрики. (Запрос, Наименование)
const validateRubric = () => {
  const { name, query } = currentEditRubric.value;

  validateRubricName(name);
  validateRubricQuery(query);
};

// Сброс ошибки наличия одинаковых наименований рубрик
const resetUniqueError = () => {
  errorList.value.nameExist = false;
};

// Создание/Обновление рубрики
const saveRubric = async () => {
  isFormSubmitted.value = true;
  resetErrorList();
  validateRubric();
  if (checkErrorList()) {
    isFormSubmitted.value = false;
    return false;
  }

  const result = await (props.isNewRubric
          ? rubricsStore.addNewRubric(currentEditRubric.value)
          : rubricsStore.saveChanges(currentEditRubric.value)
  );

  // Проверка на уникальность названия рубрики
  if (result.response && result.response.status === 409) {
    // TODO: Переписать, как добавят коды ошибок на бек.
    if (result.response.data === "Рубрика с таким именем уже существует") {
      errorList.value.nameExist = true;
    } else if (result.response.data === "Рубрика с таким запросом уже существует") {
      errorList.value.queryExist = true;
    }

    isFormSubmitted.value = false;
    return false;
  }

  // Проверка корректности запроса
  if (result.response && result.response.status === 400 && result.response.data.includes("Cannot parse")) {
    errorList.value.queryInvalid = true;
    isFormSubmitted.value = false;
    return false;
  }

  // TODO: task 1873. Конвертация в строку добавлена из разных типов id. Удалить после исправления на беке.
  rubricsStore.$patch({
    chosenThemes: [],
    chosenRubrics: [result.id.toString()],
  });

  emit("close-edit-popup");

  await newsFeedStore.clearNewsFeed();
  await newsFeedStore.getNews();


  isFormSubmitted.value = false;
  return false;
};

// Отображение уведомления постановки новости на мониторинг
const statusNotifyShowStartMonitoring = computed(() => {
  return isShowStartMonitoringNotify.value && !currentEditRubric.value.is_fixed;
});
const isShowStartMonitoringNotify = ref(true);
const closeStartMonitoringNotify = () => {
  isShowStartMonitoringNotify.value = false;
  localStorage.setItem("notify_rubrics_start_monitoring", false);
};
</script>

<template>
  <div
    v-if="currentEditRubric"
    class="rubric-aside-backdrop"
  />
  <aside
    v-if="currentEditRubric"
    class="rubric-aside"
  >
    <div class="rubric-aside__top">
      <header class="rubric-aside__header">
        <h2 class="rubric-aside__title">
          <template v-if="isNewRubric">
            {{ t('news_page_rubrics_edit_popup.title') }}
          </template>
          <template v-else>
            {{ rubric.name }}
          </template>
        </h2>

        <button
          type="button"
          :class="[
            'rubric-aside__status',
            currentEditRubric.is_fixed
              ? 'rubric-aside__status--fixed'
              : 'rubric-aside__status--active'
          ]"
          :disabled="currentEditRubric.is_fixed"
          @click="setRubricFixed"
        >
          <span
            v-if="currentEditRubric.is_fixed"
            class="rubric-aside__status-indicator"
            aria-hidden="true"
          />
          <IconEye
            v-else
            class="rubric-aside__status-icon"
            aria-hidden="true"
          />
          <span class="rubric-aside__status-text">
            {{ currentEditRubric.is_fixed ?
              t('news_page_rubrics_edit_popup.status.fixed') : t('news_page_rubrics_edit_popup.status.not_fixed') }}
          </span>
        </button>

        <button
          type="button"
          class="rubric-aside__close"
          @click="closeAside"
        >
          <IconCross
            class="rubric-aside__close-icon"
            aria-hidden="true"
          />
        </button>
      </header>

      <NotifyAlert
        v-if="statusNotifyShowStartMonitoring"
        :text="t('news_page_rubrics_edit_popup.alert.start_monitoring')"
        @close-notify="closeStartMonitoringNotify"
      />
    </div>

    <div class="rubric-aside__body">
      <!--   Наименование рубрики   -->
      <InputPrimary
        ref="inputNameRef"
        v-model="rubricName"
        :required="true"
        :alert="nameErrorMessage"
        :error="errorList.nameExist
          || errorList.nameEmoji
          || errorList.nameSymbol
          || errorList.nameRequired
          || errorList.nameMaxLength"
        :placeholder="t('news_page_rubrics_edit_popup.input.name.placeholder')"
        :label="t('news_page_rubrics_edit_popup.input.name.title')"
        @focus="resetUniqueError"
      />

      <!--   Выбор режима поиска   -->
      <!--   Скрыто перед релизом. 23.12.25   -->
      <!--      <RubricSearchType-->
      <!--        v-if="isNewRubric"-->
      <!--        v-model="currentEditRubric.type"-->
      <!--      />-->

      <!--   Параметры запроса   -->
      <RubricQueryParameters
        v-model:rubric-query="rubricQuery"
        :is-new-rubric="isNewRubric"
        :error="errorList.queryInvalid || errorList.queryExist || errorList.queryRequired || errorList.queryMaxLength"
        :error-type="getRubricQueryErrorType"
        :search-type="currentEditRubric.type"
        :query-max-length="QUERY_MAX_LENGTH"
      />
    </div>

    <div class="rubric-aside__footer">
      <div class="rubric-aside__footer-actions">
        <ButtonAccentPrimary
          class="rubric-aside__button-save"
          :disabled="isSaveButtonDisabled || isFormSubmitted"
          size="large"
          @click="saveRubric"
        >
          <template v-if="isNewRubric">
            {{ t('news_page_rubrics_edit_popup.button.create') }}
          </template>
          <template v-else>
            {{ t('news_page_rubrics_edit_popup.button.save') }}
          </template>
        </ButtonAccentPrimary>

        <ButtonNeutralSecondary
          class="rubric-aside__button-close"
          size="large"
          @click="closeAside"
        >
          {{ t('news_page_rubrics_edit_popup.button.cancel') }}
        </ButtonNeutralSecondary>
      </div>

      <ButtonAccentSecondary
        v-if="!isNewRubric"
        class="rubric-aside__button-delete"
        size="large"
        @click="emit('open-delete-popup', currentEditRubric)"
      >
        <TrashIcon />
        {{ t('news_page_rubrics_edit_popup.button.delete') }}
      </ButtonAccentSecondary>
    </div>

    <!-- Окно подтверждения изменений -->
    <Modal
      v-if="isShowConfirmModal"
      :title="t('news_page_popup_delete.title.unsaved_changes')"
      :confirm-text="t('news_page_popup_delete.button.accept_unsaved')"
      :cancel-text="t('news_page_popup_delete.button.cancel_button')"
      teleport="#app"
      @cancel="isShowConfirmModal = false"
      @confirm="emit('close-edit-popup')"
    >
      {{ t('news_page_popup_delete.message.unsaved_changes') }}
    </Modal>
  </aside>
</template>

<style scoped lang="scss">

.rubric-aside-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
  background-color: rgba(0, 0, 0, 0.5);
}

.rubric-aside {
  position: fixed;
  right: 0;
  top: 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 600px;
  height: 100%;
  padding: 20px;
  background-color: var(--background-bg-white);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);

  &__button {
    &-close {
      min-width: 133px;
    }

    &-save {
      min-width: 133px;
    }

    &-delete {
      min-width: 162px;

      svg {
        width: 14px;
        height: 14px;
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    gap: 8px;

    &-actions {
      display: flex;
      gap: 8px;
    }
  }

  &__top {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    min-height: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__title {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
    color: var(--text-text-black);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    min-width: 0;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 2px 8px;
    border: none;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 400;
    line-height: 1.4;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &--active {
      background-color: var(--background-bg-active-hover);
      color: var(--text-text-on);
      cursor: pointer;

      &:hover {
        background-color: var(--blue-blue30);
      }

      &:active {
        background-color: var(--blue-blue40, var(--blue-blue30));
      }
    }

    &--fixed {
      background-color: rgba(95, 185, 131, 0.2);
      color: #5fb983;
      cursor: default;
    }

    &-indicator {
      display: block;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #5fb983;
      flex-shrink: 0;
    }

    &-icon {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
    }

    &-text {
      white-space: nowrap;
    }
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-left: auto;
    padding: 0;
    border: none;
    background-color: transparent;
    color: var(--icon-icon-secondary);
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      color: var(--icon-icon-on);
    }

    &:active {
      color: var(--icon-icon-primary, var(--icon-icon-on));
    }

    &-icon {
      width: 20px;
      height: 20px;
    }
  }
}
</style>